import { useState } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Plug,
  FileText,
  GitCompare,
  BarChart3,
  CheckCircle2,
} from 'lucide-react';

const ONBOARDING_KEY = 'onboardingComplete';

const STEPS = [
  {
    title: 'Welcome to ADP Oracle Reconcile',
    body: 'This tool validates payroll data between Oracle Fusion HCM and ADP GlobalView before each pay cycle closes.',
    icon: CheckCircle2,
  },
  {
    title: 'Step 1 — Connect Oracle',
    body: 'Add your HCM tenant under Connections. Your consultant can help connect Oracle and install the payroll reports.',
    icon: Plug,
    action: 'connections',
    actionLabel: 'Open Connections',
  },
  {
    title: 'Step 2 — Load your data',
    body: 'Pull or upload Oracle element entries and employee master on Reports. ADP register and G2 files can be uploaded on each recon page.',
    icon: FileText,
    action: 'reports',
    actionLabel: 'Open Reports',
  },
  {
    title: 'Step 3 — Run checks',
    body: 'Use Pre-Payroll for banking, email, SSL, and employment checks. Run Payroll Recon to compare Oracle amounts against the ADP register.',
    icon: GitCompare,
    action: 'recon',
    actionLabel: 'Open Payroll Recon',
  },
  {
    title: 'Step 4 — Review results',
    body: 'Exceptions appear on Results. Export to Excel, fix issues in Oracle or ADP, then re-run. Past runs are saved by pay period.',
    icon: BarChart3,
    action: 'results',
    actionLabel: 'Open Results',
  },
];

export function isOnboardingComplete() {
  return localStorage.getItem(ONBOARDING_KEY) === '1';
}

export function completeOnboarding() {
  localStorage.setItem(ONBOARDING_KEY, '1');
}

export default function OnboardingWizard({ onNavigate, onComplete }) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  const finish = () => {
    completeOnboarding();
    onComplete?.();
  };

  return (
    <div className="onboarding-backdrop" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
      <div className="onboarding-panel">
        <div className="flex items-start justify-between gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-950/60 ring-1 ring-cyan-700/40">
            <Icon className="h-5 w-5 text-cyan-400" />
          </div>
          <button
            type="button"
            onClick={finish}
            className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-300"
            aria-label="Close onboarding"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-cyan-500/90">
          Getting started · {step + 1} of {STEPS.length}
        </p>
        <h2 id="onboarding-title" className="mt-1 text-xl font-bold text-slate-100">
          {current.title}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">{current.body}</p>

        <div className="mt-4 flex gap-1">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-cyan-500' : 'bg-slate-700'}`}
            />
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="btn-secondary py-1.5 text-sm disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <div className="flex flex-wrap gap-2">
            {current.action && (
              <button
                type="button"
                onClick={() => {
                  onNavigate?.(current.action);
                  finish();
                }}
                className="btn-secondary py-1.5 text-sm"
              >
                {current.actionLabel}
              </button>
            )}
            {isLast ? (
              <button type="button" onClick={finish} className="btn-primary py-1.5 text-sm">
                Get started
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                className="btn-primary py-1.5 text-sm"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <button type="button" onClick={finish} className="mt-4 text-xs text-slate-500 hover:text-slate-300">
          Skip tour — I know the workflow
        </button>
      </div>
    </div>
  );
}
